import { useState, useEffect, useRef } from "react";
import Footer from "./Footer";
import config from "../config";

const { API_BASE, UPLOADS_URL } = config;

async function fetchJSON(url) {
  const res = await fetch(url);
  if (!res.ok) throw new Error(`HTTP ${res.status} – ${url}`);
  return res.json();
}

/* ── Chunk array into groups of n (for 2-per-slide pagination) ── */
function chunk(arr, n) {
  const out = [];
  for (let i = 0; i < arr.length; i += n) out.push(arr.slice(i, i + n));
  return out;
}

/* ── Logo Ticker (ported from ContactPage's VideoSuppliersSection) ── */
function SupplierLogoItem({ supplier }) {
  const logoUrl = `${UPLOADS_URL}/suppliers/${supplier.logo_filename}`;

  return (
    <div className="ts-ticker__logo-item" aria-label={supplier.name}>
      <img
        src={logoUrl}
        alt={supplier.name}
        className="ts-ticker__logo-img"
        draggable="false"
      />
    </div>
  );
}

function LogoTicker({ suppliers }) {
  const items = [...suppliers, ...suppliers, ...suppliers];
  return (
    <div className="ts-ticker__wrap">
      <div className="ts-ticker__track">
        {items.map((s, i) => (
          <SupplierLogoItem key={`${s.id}-${i}`} supplier={s} />
        ))}
      </div>
    </div>
  );
}

/* ── Counter stats data ── */
const COUNTER_STATS = [
  { id: 1, icon: "counter-1.png", value: 1090, label: "Happy Clients" },
  { id: 2, icon: "counter-2.png", value: 491, label: "Finished Projects" },
  { id: 3, icon: "counter-3.png", value: 45, label: "Skilled Experts" },
  { id: 4, icon: "counter-4.png", value: 890, label: "Media Posts" },
];

/* ── Single animated counter cell ── */
function CounterItem({ icon, value, label, animate }) {
  const [count, setCount] = useState(0);

  useEffect(() => {
    if (!animate) return;
    const duration = 1400;
    const start = performance.now();

    let raf;
    const tick = (now) => {
      const progress = Math.min((now - start) / duration, 1);
      const eased = 1 - Math.pow(1 - progress, 3);
      setCount(Math.round(eased * value));
      if (progress < 1) raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [animate, value]);

  return (
    <div className="ts-counter__item">
      <div className="ts-counter__icon-wrap">
        <img
          src={`${UPLOADS_URL}/svg-icons/${icon}`}
          alt=""
          aria-hidden="true"
          className="ts-counter__icon"
        />
      </div>
      <div className="ts-counter__body">
        <span className="ts-counter__value">{count.toLocaleString()}</span>
        <span className="ts-counter__label">{label}</span>
      </div>
    </div>
  );
}

/* ── Counter stats section: animates on scroll into view ── */
function CounterStats() {
  const [animate, setAnimate] = useState(false);
  const sectionRef = useRef(null);

  useEffect(() => {
    const node = sectionRef.current;
    if (!node) return;
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setAnimate(true);
          observer.disconnect();
        }
      },
      { threshold: 0.3 }
    );
    observer.observe(node);
    return () => observer.disconnect();
  }, []);

  return (
    <div className="ts-counter" ref={sectionRef}>
      <div className="ts-counter__inner">
        {COUNTER_STATS.map((s) => (
          <CounterItem key={s.id} icon={s.icon} value={s.value} label={s.label} animate={animate} />
        ))}
      </div>
    </div>
  );
}

/* ── Star rating ── */
function Stars({ rating }) {
  const r = Math.max(0, Math.min(5, Math.round(Number(rating) || 0)));
  if (!r) return null;
  return (
    <div className="ts-card__stars" aria-label={`${r} out of 5 stars`}>
      {Array.from({ length: 5 }).map((_, i) => (
        <svg key={i} width="13" height="13" viewBox="0 0 20 20"
          fill={i < r ? "#f5a623" : "none"} stroke={i < r ? "#f5a623" : "#d8dce6"} strokeWidth="1.2">
          <path d="M10 1.5l2.6 5.4 5.9.8-4.3 4.2 1 5.9L10 14.9l-5.2 2.9 1-5.9-4.3-4.2 5.9-.8L10 1.5z" />
        </svg>
      ))}
    </div>
  );
}

/* ── Single testimonial card ── */
function TestimonialCard({ t }) {
  return (
    <div className="ts-card">
      <div className="ts-card__top">
        {t.photo_filename ? (
          <img
            src={`${UPLOADS_URL}/testimonials/${t.photo_filename}`}
            alt={t.name}
            className="ts-card__photo"
          />
        ) : (
          <div className="ts-card__photo ts-card__photo-fallback" />
        )}

        <span className="ts-card__quote-icon" aria-hidden="true">
          <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
            <path d="M3 11.5a6.5 6.5 0 0 1 9.8-5.6 5.5 5.5 0 0 1 7.7 6.6 5.5 5.5 0 0 1-7 7
                     6.5 6.5 0 0 1-9-3.4"
              stroke="#029add" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
            <circle cx="9.5" cy="11" r="0.9" fill="#029add" />
            <circle cx="13" cy="11" r="0.9" fill="#029add" />
            <circle cx="16.5" cy="11" r="0.9" fill="#029add" />
          </svg>
        </span>
      </div>

      <p className="ts-card__content">{t.content}</p>

      <Stars rating={t.rating} />

      <div className="ts-card__byline">
        <span className="ts-card__name">{t.name}</span>
        {(t.position || t.company) && (
          <span className="ts-card__role">
            {" "}/ {t.position}{t.position && t.company ? ", " : ""}{t.company}
          </span>
        )}
      </div>
    </div>
  );
}

/* ── Main Page ── */
function TestimonialsPage() {
  const [testimonials, setTestimonials] = useState([]);
  const [suppliers, setSuppliers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [slide, setSlide] = useState(0);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);

    Promise.all([
      fetchJSON(`${API_BASE}/testimonials.php`),
      fetchJSON(`${API_BASE}/video-suppliers.php`).catch(() => ({ suppliers: [] })),
    ])
      .then(([testimonialsData, vsData]) => {
        if (cancelled) return;
        setTestimonials(Array.isArray(testimonialsData) ? testimonialsData : []);
        setSuppliers(vsData?.suppliers || []);
        setLoading(false);
      })
      .catch((err) => {
        if (cancelled) return;
        console.error("TestimonialsPage load error:", err);
        setError("Failed to load testimonials.");
        setLoading(false);
      });

    return () => { cancelled = true; };
  }, []);

  const slides = chunk(testimonials, 2);
  const slideCount = slides.length;

  useEffect(() => {
    if (slide >= slideCount) setSlide(0);
  }, [slideCount, slide]);

  const goTo = (i) => setSlide(((i % slideCount) + slideCount) % slideCount);

  return (
    <>
    <div className="ts">

      {/* ── HERO ── */}
      <div className="ts-hero">
        <img
          src={`${UPLOADS_URL}/hero/hero-01.png`}
          alt=""
          aria-hidden="true"
          className="ts-hero__img"
        />
        <div className="ts-hero__overlay" />
        <div className="ts-hero__content">
          <nav className="ts-bread" aria-label="Breadcrumb">
            <a href="/" className="ts-bread__link">Home</a>
            <span className="ts-bread__sep" aria-hidden="true">›</span>
            <span className="ts-bread__current">Testimonials</span>
          </nav>
          <h1 className="ts-hero__title">Testimonials</h1>
          <p className="ts-hero__subtitle">What our clients say about working with us</p>
        </div>
      </div>

      {/* ── COUNTER STATS ── */}
      <CounterStats />

      {/* ── BODY ── */}
      <div className="ts-body">
        <div className="ts-body__inner">

          {loading && <div className="ts-state">Loading testimonials…</div>}

          {!loading && error && (
            <div className="ts-state ts-state--error">{error}</div>
          )}

          {!loading && !error && testimonials.length === 0 && (
            <div className="ts-state">No testimonials available yet.</div>
          )}

          {!loading && !error && testimonials.length > 0 && (
            <>
              <div className="ts-track">
                {slides[slide].map((t) => (
                  <TestimonialCard key={t.id} t={t} />
                ))}
              </div>

              {slideCount > 1 && (
                <div className="ts-dots" role="tablist" aria-label="Testimonial slides">
                  {slides.map((_, i) => (
                    <button
                      key={i}
                      type="button"
                      role="tab"
                      aria-selected={i === slide}
                      aria-label={`Go to slide ${i + 1}`}
                      className={`ts-dot ${i === slide ? "ts-dot--active" : ""}`}
                      onClick={() => goTo(i)}
                    />
                  ))}
                </div>
              )}
            </>
          )}

        </div>
      </div>

      {/* ── SUPPLIER LOGO TICKER BAR ── */}
      {suppliers.length > 0 && (
        <div className="ts-ticker__bar">
          <LogoTicker suppliers={suppliers} />
        </div>
      )}

      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Roboto:wght@400;500;600;700;800;900&display=swap');

        .ts *, .ts *::before, .ts *::after { box-sizing: border-box; margin: 0; padding: 0; }
        .ts a { text-decoration: none; }
        .ts { font-family: 'Roboto', sans-serif; background: #f5f7fa; }

        /* ── HERO ── */
        .ts-hero {
          position: relative; height: 260px; overflow: hidden;
          background: #013d97;
        }
        .ts-hero__img {
          width: 100%; height: 100%; object-fit: cover; object-position: center;
          display: block;
        }
        .ts-hero__overlay {
          position: absolute; inset: 0;
          background: linear-gradient(135deg, rgba(1,21,70,.88) 0%, rgba(1,61,151,.7) 70%, rgba(2,154,221,.55) 140%);
        }
        .ts-hero__content {
          position: absolute; inset: 0;
          display: flex; flex-direction: column; justify-content: flex-end;
          padding: 32px 40px; max-width: 1280px; margin: 0 auto; left: 0; right: 0;
        }
        .ts-bread { display: flex; align-items: center; gap: 6px; flex-wrap: wrap; margin-bottom: 14px; }
        .ts-bread__link { font-size: 13px; font-weight: 600; color: rgba(255,255,255,.85); background: rgba(255,255,255,.12); padding: 3px 10px; border-radius: 3px; transition: background .2s; }
        .ts-bread__link:hover { background: rgba(255,255,255,.22); }
        .ts-bread__sep { color: rgba(255,255,255,.55); font-size: 14px; }
        .ts-bread__current { font-size: 13px; color: rgba(255,255,255,.7); }
        .ts-hero__title { font-size: clamp(26px, 3.2vw, 40px); font-weight: 900; color: #fff; line-height: 1.15; margin-bottom: 10px; }
        .ts-hero__subtitle { font-size: 15px; color: rgba(255,255,255,.78); max-width: 540px; line-height: 1.6; }

        /* ── BODY ── */
        .ts-body { padding: 56px 0 80px; }
        .ts-body__inner { max-width: 1280px; margin: 0 auto; padding: 0 40px; }

        .ts-state {
          text-align: center; padding: 64px 24px; font-size: 14px;
          color: #8890a0; background: #fff; box-shadow: 0 2px 12px rgba(0,0,0,.06);
        }
        .ts-state--error { color: #c0392b; }

        /* ── TRACK ── */
        .ts-track {
          display: grid;
          grid-template-columns: repeat(2, 1fr);
          gap: 32px;
        }

        /* ── CARD ── */
        .ts-card {
          background: #fff; border-radius: 6px; padding: 32px 36px 28px;
          box-shadow: 0 4px 20px rgba(1,21,70,.06);
        }

        .ts-card__top {
          display: flex; align-items: flex-start; justify-content: space-between;
          margin-bottom: 22px;
        }

        .ts-card__photo {
          width: 64px; height: 64px; border-radius: 8px; object-fit: cover;
          display: block;
          box-shadow: 6px 6px 0 0 #029add;
        }
        .ts-card__photo-fallback { background: linear-gradient(135deg, #013d97, #029add); }

        .ts-card__quote-icon {
          width: 44px; height: 44px; border-radius: 50%;
          border: 1.5px solid #029add;
          display: flex; align-items: center; justify-content: center;
          flex-shrink: 0;
        }

        .ts-card__content {
          font-size: 16px; line-height: 1.65; color: #3a3f4d;
          margin-bottom: 20px;
        }

        .ts-card__stars { display: flex; gap: 3px; margin-bottom: 14px; }

        .ts-card__byline { font-size: 14px; }
        .ts-card__name { font-weight: 700; color: #1a1a2e; }
        .ts-card__role { color: #8890a0; }

        /* ── DOTS ── */
        .ts-dots {
          display: flex; align-items: center; justify-content: center;
          gap: 8px; margin-top: 40px;
        }
        .ts-dot {
          width: 9px; height: 9px; border-radius: 50%;
          background: #d8dce6; border: none; cursor: pointer;
          padding: 0; transition: background .2s, transform .2s;
        }
        .ts-dot:hover { background: #aab1c2; }
        .ts-dot--active {
          background: #fff; border: 2px solid #029add;
          width: 13px; height: 13px;
        }

        /* ── COUNTER STATS ── */
        .ts-counter {
          background: #fff;
          border-top: 1px solid #e8eaf0;
          border-bottom: 1px solid #e8eaf0;
        }
        .ts-counter__inner {
          max-width: 1280px; margin: 0 auto;
          display: grid;
          grid-template-columns: repeat(4, 1fr);
        }
        .ts-counter__item {
          display: flex; align-items: center; gap: 20px;
          padding: 48px 40px;
          border-right: 1px solid #e8eaf0;
        }
        .ts-counter__item:last-child { border-right: none; }
        .ts-counter__icon-wrap {
          width: 72px; height: 72px; flex-shrink: 0; border-radius: 12px;
          background: #fff;
          box-shadow: 0 6px 20px rgba(1,21,70,.08);
          display: flex; align-items: center; justify-content: center;
        }
        .ts-counter__icon { width: 34px; height: 34px; object-fit: contain; }
        .ts-counter__body { display: flex; flex-direction: column; gap: 4px; min-width: 0; }
        .ts-counter__value { font-size: 34px; font-weight: 900; color: #029add; line-height: 1.1; }
        .ts-counter__label { font-size: 12.5px; font-weight: 800; letter-spacing: .04em; color: #5a5f72; text-transform: uppercase; }

        /* ── SUPPLIER TICKER BAR ── */
        .ts-ticker__bar {
          background: #fff;
          border-top: 1px solid #e8eaf0;
          box-shadow: 0 -2px 20px rgba(0,0,0,.06);
          overflow: hidden;
        }
        .ts-ticker__wrap {
          overflow: hidden;
          width: 100%;
          padding: 14px 0;
          -webkit-mask-image: linear-gradient(to right, transparent 0%, #000 8%, #000 92%, transparent 100%);
          mask-image: linear-gradient(to right, transparent 0%, #000 8%, #000 92%, transparent 100%);
        }
        .ts-ticker__track {
          display: flex;
          align-items: center;
          width: max-content;
          animation: ts-ticker-scroll 38s linear infinite;
        }
        .ts-ticker__track:hover { animation-play-state: paused; }
        @keyframes ts-ticker-scroll {
          0%   { transform: translateX(0); }
          100% { transform: translateX(-33.333%); }
        }
        .ts-ticker__logo-item {
          position: relative;
          display: flex; align-items: center; justify-content: center;
          padding: 10px 36px;
          border-right: 1px solid #e8eaf0;
          cursor: pointer;
          flex-shrink: 0;
          transition: background 0.2s;
        }
        .ts-ticker__logo-item:hover { background: #f7f9ff; }
        .ts-ticker__logo-img {
          height: 36px; width: auto; max-width: 140px;
          object-fit: contain;
          filter: grayscale(1) opacity(.65);
          transition: filter 0.25s;
          user-select: none; pointer-events: none;
        }
        .ts-ticker__logo-item:hover .ts-ticker__logo-img { filter: grayscale(0) opacity(1); }

        /* ── RESPONSIVE ── */
        @media (max-width: 768px) {
          .ts-hero { height: 200px; }
          .ts-hero__content { padding: 20px 20px; }
          .ts-body { padding: 40px 0 64px; }
          .ts-body__inner { padding: 0 20px; }
          .ts-track { grid-template-columns: 1fr; gap: 20px; }
          .ts-card { padding: 26px 24px 22px; }
          .ts-counter__inner { grid-template-columns: 1fr 1fr; }
          .ts-counter__item {
            padding: 28px 20px;
            border-bottom: 1px solid #e8eaf0;
          }
          .ts-counter__item:nth-child(2n) { border-right: none; }
          .ts-counter__icon-wrap { width: 56px; height: 56px; border-radius: 10px; }
          .ts-counter__icon { width: 26px; height: 26px; }
          .ts-counter__value { font-size: 26px; }
          .ts-ticker__logo-item { padding: 10px 20px; }
          .ts-ticker__logo-img { height: 26px; max-width: 110px; }
        }

        a:focus-visible, button:focus-visible { outline: 2px solid #029add; outline-offset: 2px; }
        @media (prefers-reduced-motion: reduce) {
          .ts *, .ts *::before, .ts *::after { transition-duration: 0ms !important; }
          .ts-ticker__track { animation: none; }
        }
      `}</style>
    </div>
    <Footer />
    </>
  );
}

export default TestimonialsPage;